import { useState, useRef } from 'react';
import { ApiRequest, ToastBuilder, asyncPipeline } from '../../../../../../../GlobalComponents/Builders';
import { buildOptionsFromArray, groupObjectsByKey, rekeyArray, createValuesArray, parseJSONSafe } from '../../../../../../../GlobalComponents/Helpers';

const useDMTData = (jobData, jobId) => {

    const jobEventSource = jobData?.source_connection?.service_code || null;
    const jobEventDestination = jobData?.destination_connection?.service_code || null;

    const [mappingJobs, setMappingJobs] = useState([])
    const [jobReport, setJobReport] = useState([])
    const [allJobs, setAllJobs] = useState([])

    const [jobResources, setJobResources] = useState([]);
    const [mappedResources, setMappedResources] = useState({});
    const jobResourceMappings = useRef([])

    const [jobKeyMappings, setJobKeyMappings] = useState(null);
    const [jobKeyMappingsLength, setJobKeyMappingsLength] = useState(0);
    const [showJobKeyMappings, setShowJobKeyMappings] = useState(false);
    const [showDeletedJobKeyMappings, setShowDeletedJobKeyMappings] = useState('active');
    const [keyMappingSummary, setKeyMappingSummary] = useState(null);

    const [jobValueMappings, setJobValueMappings] = useState(null);
    const [jobValueMappingsLength, setJobValueMappingsLength] = useState(0);
    const [showJobValueMappings, setShowJobValueMappings] = useState(false);
    const [valueMappingSummary, setValueMappingSummary] = useState(null);
    const [jobValueMappingDestinationUsage, setJobValueMappingDestinationUsage] = useState({});

    const [supplementaryData, setSupplementaryData] = useState(null);
    const [supplementaryDataLength, setSupplementaryDataLength] = useState(0);
    const [showSupplementaryData, setShowSupplementaryData] = useState(false);
    const [compositeChains, setCompositeChains] = useState([]);

    const [availableResources, setAvailableResources] = useState({});
    const [showResources, setShowResources] = useState(false);
    const [showResourceMappings, setShowResourceMappings] = useState(false);

    const [destinationConnectionKeys, setDestinationConnectionKeys] = useState([]);
    const [allDestinationConnectionKeys, setAllDestinationConnectionKeys] = useState([]);
    const [destinationConnectionValues, setDestinationConnectionValues] = useState([]);
    const [allDestinationConnectionValues, setAllDestinationConnectionValues] = useState([]);
    const [destinationResourceParentScoped, setDestinationResourceParentScoped] = useState({});

    const [mappingCodes, setMappingCodes] = useState([])
    const [mappingCodeOptions, setMappingCodeOptions] = useState([])
    const [dmtStates, setDmtStates] = useState([])
    const [dmtCompositeStates, setDmtCompositeStates] = useState([])
    const [isArchived, setIsArchived] = useState(false)
    const [defaultResourceMappings, setDefaultResourceMappings] = useState([])

    const [renderPage, setRenderPage] = useState(false);
    const [showAccordionResource, setShowAccordionResource] = useState({});
    const [dataPreview, setDataPreview] = useState(null);
    const [keyResourceLookup, setKeyResourceLookup] = useState([]);

    // Accordion toggles — tracked by accordionIdentifier (e.g. "123-456") instead of index
    const [isOpen, setIsOpen] = useState(null);
    const [isOpen2, setIsOpen2] = useState(null);
    const [isOpen3, setIsOpen3] = useState(0);
    const toggle = (id) => (isOpen === id ? setIsOpen(null) : setIsOpen(id));
    const toggle2 = (id) => (isOpen2 === id ? setIsOpen2(null) : setIsOpen2(id));
    const toggle3 = (id) => (isOpen3 === id ? setIsOpen3(null) : setIsOpen3(id));

    // ─── Fetch Functions ───

    const getMappingCodes = () => {
        ApiRequest('GET', '/dmt/settings').then(function (results) {
            let codes = []
            let states = []
            let compositeStates = []
            if (results?.data?.length > 0) {
                codes = parseJSONSafe(results.data[0].codes)
                states = parseJSONSafe(results.data[0].states)
                compositeStates = parseJSONSafe(results.data[0].composite_states)
            }
            setMappingCodes(codes)
            setMappingCodeOptions(buildOptionsFromArray(codes, { label: 'code', value: 'code_id' }).map(o => ({ ...o, label: `${o.value} - ${o.label}` })))
            setDmtStates(states)
            setDmtCompositeStates(Array.isArray(compositeStates) ? compositeStates : [])
        })
    }

    const getDefaultResourceMappings = () => {
        ApiRequest('GET', '/dmt/defaults/resourceMenu').then(res => {
            setDefaultResourceMappings(res.data || [])
        })
    }

    const fetchDMTJobs = () => {
        if (allJobs.length > 0) return
        ApiRequest('GET', '/tools/{toolId}/jobs', { pathParams: { toolId: 'data-migration' } }).then(function (results) {
            setAllJobs(results.data?.length > 0 ? results.data.filter(job => job.partner_id === jobData.partner_id && job.job_type === 'two-way-sync' && parseInt(job.job_id) !== parseInt(jobData.job_id)) : [])
        })
    }

    const fetchResources = async (fetchServices = true) => {
        const result = await new Promise((resolve) => {
            var serviceId = jobData?.source_connection?.service_id || null
            if (!serviceId) return false

            ApiRequest('GET', '/dmt/jobs/{jobId}/resources', { pathParams: { jobId } })
                .then(function (results) {
                    setJobResources(results.data && Object.entries(results.data).length > 0 ? rekeyArray(results.data, 'connection_id') : {})
                })
                .then(function () {
                    let services = JSON.stringify([
                        `${jobData.source_connection.id}-${jobData.source_connection.service_id}`,
                        `${jobData.destination_connection.id}-${jobData.destination_connection.service_id}`
                    ]);
                    let temp = {};

                    if (!fetchServices) return resolve(true)
                    ApiRequest('GET', '/dmt/jobs/{jobId}/services', { pathParams: { jobId }, queryParams: { services } }).then(function (results) {
                        if (results.data) {
                            results.data.forEach((service) => {
                                Object.entries(service).forEach(([k, v]) => {
                                    temp[`${k}`] = v
                                })
                            })
                            setAvailableResources(temp)
                            return resolve(true)
                        } else {
                            return resolve(false)
                        }
                    })
                })
        })
        return result
    }

    const fetchKeyMappings = async (sourceResourceId = null) => {
        const isTargeted = sourceResourceId !== null && sourceResourceId !== true
        const queryParams = isTargeted ? { sourceResourceId } : null
        const result = await new Promise((resolve) => {
            ApiRequest('GET', '/dmt/jobs/{jobId}/keymappings', { pathParams: { jobId: jobData.id }, queryParams: queryParams }).then(function (results) {
                if (!results.data) return resolve(false)

                if (results.data.length > 0) {
                    setShowAccordionResource(prev => {
                        const keyAccordionState = results.data.reduce((accordion, item) => {
                            const accordionIdentifier = `${item.source_resource_id}-${item.destination_resource_id}`;
                            return { ...accordion, [accordionIdentifier]: true };
                        }, {});
                        const updatedState = { ...prev };
                        Object.keys(keyAccordionState).forEach(key => {
                            updatedState[key] = keyAccordionState[key];
                        });
                        return updatedState;
                    });
                }

                if (isTargeted) {
                    // Merge: replace only the affected resource group
                    setJobKeyMappings(prev => {
                        if (!prev) return results.data
                        const filtered = prev.filter(m => String(m.source_resource_id) !== String(sourceResourceId))
                        // Find where the old group started to preserve ordering
                        const insertIdx = prev.findIndex(m => String(m.source_resource_id) === String(sourceResourceId))
                        const merged = insertIdx >= 0
                            ? [...filtered.slice(0, insertIdx), ...results.data, ...filtered.slice(insertIdx)]
                            : [...filtered, ...results.data]
                        setJobKeyMappingsLength(merged.length)
                        return merged
                    })
                } else {
                    setJobKeyMappingsLength(results.data.length)
                    if (results.data.length > 0) {
                        setJobKeyMappings(results.data)
                    }
                }
                return resolve(true)
            })
        })
        setShowJobKeyMappings(result)
        return result
    }

    const fetchKeyMappingSummary = async () => {
        const result = await new Promise((resolve) => {
            ApiRequest('GET', '/dmt/jobs/{jobId}/keymappings', { pathParams: { jobId: jobData.id }, queryParams: { summary: 'true' } }).then(function (results) {
                if (!results.data) return resolve(false)
                setKeyMappingSummary(results.data)
                setJobKeyMappingsLength(results.data.reduce((sum, r) => sum + r.total_count, 0))

                if (results.data.length > 0) {
                    setShowAccordionResource(prev => {
                        const keyAccordionState = results.data.reduce((accordion, item) => {
                            return { ...accordion, [`${item.source_resource_id}-${item.destination_resource_id}`]: true };
                        }, {});
                        return { ...prev, ...keyAccordionState };
                    });
                }
                return resolve(true)
            })
        })
        setShowJobKeyMappings(result)
        return result
    }

    const fetchDestinationResourceKeys = async (resourceMappings = [], accordionIdentifier = null) => {
        const result = new Promise((resolve) => {
            const destinationConnectionId = jobData?.destination_connection?.id || null
            if (!destinationConnectionId) return resolve(false)

            const queryParams = { destinationConnectionId, jobId: jobData.id, syncJobId: jobData.sync_job_id > 0 ? jobData.sync_job_id : jobData.id }

            ApiRequest('GET', '/dmt/jobs/{jobId}/resources/keys', { pathParams: { jobId }, queryParams: queryParams })
                .then(function (results) {
                    if (results.data && Object.entries(results.data).length > 0) {
                        var keyCleanMapping = {}
                        var keyCleanMappingAll = {}
                        var destinationKeysByResourceId = groupObjectsByKey(results.data, 'resource_id')
                        resourceMappings = resourceMappings.length > 0 ? resourceMappings : jobResourceMappings.current

                        if (!(resourceMappings.length > 0)) return resolve(false)

                        Object.keys(destinationKeysByResourceId).forEach((resourceId, index) => {
                            var destinationJobMappingsByResourceId = groupObjectsByKey(resourceMappings, 'destination_resource_id')

                            if (destinationJobMappingsByResourceId[resourceId]) {
                                keyCleanMapping[resourceId] = {}
                                destinationJobMappingsByResourceId[resourceId].forEach((resourceMap) => {
                                    destinationKeysByResourceId[resourceMap.destination_resource_id] = destinationKeysByResourceId[resourceMap.destination_resource_id] ? destinationKeysByResourceId[resourceMap.destination_resource_id] : []
                                    keyCleanMapping[resourceId] = Object.assign({}, keyCleanMapping[resourceId], buildOptionsFromArray(destinationKeysByResourceId[resourceMap.destination_resource_id], { label: 'key', value: 'resource_key_id', groupKey: 'resource_name', groupLabel: '' }))
                                    keyCleanMappingAll = Object.assign({}, keyCleanMappingAll, buildOptionsFromArray(destinationKeysByResourceId[resourceMap.destination_resource_id], { label: 'key', value: 'resource_key_id', groupKey: 'resource_name', groupLabel: '' }))
                                })
                            }

                            if (index + 1 === Object.keys(destinationKeysByResourceId).length) {
                                setDestinationConnectionKeys(keyCleanMapping)
                                setAllDestinationConnectionKeys(keyCleanMappingAll)
                                if (accordionIdentifier) {
                                    setShowAccordionResource(prev => ({ ...prev, [accordionIdentifier]: true }));
                                }
                                return resolve(true)
                            }
                        })
                        return resolve(true)
                    } else {
                        return resolve(false)
                    }
                })
                .catch(function (err) {
                    console.error(err.message);
                    ToastBuilder('error');
                    return resolve(false)
                })
        })
        return result
    }

    const fetchKeyResourceLookup = () => {
        ApiRequest('GET', '/dmt/jobs/{jobId}/worksheetDefaults', { pathParams: { jobId: jobData.id } }).then(function (results) {
            setKeyResourceLookup(results?.data || [])
        })
    }

    const fetchSupplementaryData = async () => {
        const result = await new Promise((resolve) => {
            ApiRequest('GET', '/dmt/jobs/{jobId}/supplementaryData', { pathParams: { jobId: jobData.id } }).then(function (results) {
                console.log('[fetchSupplementaryData] raw results:', results)
                console.log('[fetchSupplementaryData] results.data:', results.data)
                if (!results.data) return resolve(false)

                const data = results.data;
                const resources = Array.isArray(data) ? data : (Array.isArray(data.resources) ? data.resources : []);
                const chains = data.compositeChains || [];

                console.log('[fetchSupplementaryData] resources:', resources, 'chains:', chains)

                setSupplementaryDataLength(resources.length + chains.length)
                setCompositeChains(chains)

                if (resources.length > 0 || chains.length > 0) {
                    setSupplementaryData(resources)
                    return resolve(true)
                } else {
                    return resolve(true)
                }
            })
        })
        setShowSupplementaryData(result)
        return result
    }

    const fetchValueMappings = async (sourceResourceId = null) => {
        const isTargeted = sourceResourceId !== null && sourceResourceId !== true
        const queryParams = isTargeted ? { sourceResourceId } : null
        const result = await new Promise((resolve) => {
            ApiRequest('GET', '/dmt/jobs/{jobId}/valuemappings', { pathParams: { jobId: jobData.id }, queryParams: queryParams }).then(function (results) {
                if (!results.data) return resolve(false)

                // Response shape: { mappings: [...], metadata: { destinationUsage: {...} } }
                // Tolerate the legacy bare-array shape too (backend may not be redeployed yet).
                const mappings = Array.isArray(results.data) ? results.data : (results.data.mappings || [])
                const destinationUsage = Array.isArray(results.data) ? {} : (results.data.metadata?.destinationUsage || {})

                if (mappings.length > 0) {
                    setShowAccordionResource(prev => {
                        const valueAccordionState = mappings.reduce((accordion, item) => {
                            return { ...accordion, [`${item.source_resource_id}-${item.destination_resource_id}`]: true };
                        }, {});
                        const updatedState = { ...prev };
                        Object.keys(valueAccordionState).forEach(key => {
                            updatedState[key] = valueAccordionState[key];
                        });
                        return updatedState;
                    });

                    mappings.forEach((valueMapping) => {
                        valueMapping.display_source_value = `${valueMapping.source_value_parent_name ? '[' + valueMapping.source_value_parent_name + '] ' : ''}${valueMapping.source_value}`;
                    })
                }

                if (isTargeted) {
                    // Merge only the refetched pairs' usage into existing state
                    setJobValueMappingDestinationUsage(prev => ({ ...prev, ...destinationUsage }))
                    setJobValueMappings(prev => {
                        if (!prev) return mappings
                        const filtered = prev.filter(m => String(m.source_resource_id) !== String(sourceResourceId))
                        const insertIdx = prev.findIndex(m => String(m.source_resource_id) === String(sourceResourceId))
                        const merged = insertIdx >= 0
                            ? [...filtered.slice(0, insertIdx), ...mappings, ...filtered.slice(insertIdx)]
                            : [...filtered, ...mappings]
                        setJobValueMappingsLength(merged.length)
                        return merged
                    })
                } else {
                    setJobValueMappingDestinationUsage(destinationUsage)
                    setJobValueMappingsLength(mappings.length)
                    if (mappings.length > 0) {
                        setJobValueMappings(mappings)
                    }
                }
                return resolve(true)
            })
        })
        setShowJobValueMappings(result)
        return result
    }

    const fetchValueMappingSummary = async () => {
        const result = await new Promise((resolve) => {
            ApiRequest('GET', '/dmt/jobs/{jobId}/valuemappings', { pathParams: { jobId: jobData.id }, queryParams: { summary: 'true' } }).then(function (results) {
                if (!results.data) return resolve(false)
                setValueMappingSummary(results.data)
                setJobValueMappingsLength(results.data.reduce((sum, r) => sum + r.total_count, 0))

                if (results.data.length > 0) {
                    setShowAccordionResource(prev => {
                        const valueAccordionState = results.data.reduce((accordion, item) => {
                            return { ...accordion, [`${item.source_resource_id}-${item.destination_resource_id}`]: true };
                        }, {});
                        return { ...prev, ...valueAccordionState };
                    });
                }
                return resolve(true)
            })
        })
        setShowJobValueMappings(result)
        return result
    }

    const fetchDestinationResourceValues = async (resourceMappings = [], accordionIdentifier = null) => {
        const result = await new Promise((resolve) => {
            const sourceConnectionId = jobData?.source_connection?.id || null
            const destinationConnectionId = jobData?.destination_connection?.id || null
            if (!destinationConnectionId || !sourceConnectionId) return resolve(false)

            const queryParams = { sourceConnectionId, destinationConnectionId, jobId: jobData.id, syncJobId: jobData.sync_job_id > 0 ? jobData.sync_job_id : jobData.id }

            ApiRequest('GET', '/dmt/jobs/{jobId}/resources/values', { pathParams: { jobId: jobData.id }, queryParams: queryParams }).then(function (results) {
                if (results.data && Object.entries(results.data).length > 0) {
                    var valueCleanMapping = {}
                    var valueCleanMappingAll = {}
                    var destinationValuesByResourceId = groupObjectsByKey(results.data, 'resource_id')
                    resourceMappings = resourceMappings.length > 0 ? resourceMappings : jobResourceMappings.current

                    if (!(resourceMappings.length > 0)) return resolve(false)

                    // Flag a destination resource as "parent-scoped" when >= 50% of its
                    // values have a parent_id (e.g. board-scoped statuses). Used to decide
                    // whether to offer the "Ignore Destination's Parent" option for the group.
                    const parentScoped = {}
                    Object.keys(destinationValuesByResourceId).forEach((rid) => {
                        const vals = destinationValuesByResourceId[rid] || []
                        if (vals.length === 0) return
                        const withParent = vals.filter(v => v.parent_id !== null && v.parent_id !== undefined && v.parent_id !== '').length
                        parentScoped[rid] = (withParent / vals.length) >= 0.5
                    })
                    setDestinationResourceParentScoped(parentScoped)

                    Object.keys(destinationValuesByResourceId).forEach((resourceId, index) => {
                        var destinationJobMappingsByResourceId = groupObjectsByKey(resourceMappings, 'destination_resource_id')

                        if (destinationJobMappingsByResourceId[resourceId]) {
                            valueCleanMapping[resourceId] = {}
                            destinationJobMappingsByResourceId[resourceId].forEach((resourceMap) => {
                                destinationValuesByResourceId[resourceMap.destination_resource_id] = destinationValuesByResourceId[resourceMap.destination_resource_id] ? destinationValuesByResourceId[resourceMap.destination_resource_id] : []
                                valueCleanMapping[resourceId] = Object.assign({}, valueCleanMapping[resourceId], buildOptionsFromArray(destinationValuesByResourceId[resourceMap.destination_resource_id], { label: 'value', value: 'resource_value_id', groupKey: 'name', groupLabel: '' }))
                                valueCleanMappingAll = Object.assign({}, valueCleanMappingAll, buildOptionsFromArray(destinationValuesByResourceId[resourceMap.destination_resource_id], { label: 'value', value: 'resource_value_id', groupKey: 'name', groupLabel: '' }))
                            })
                        }

                        if (index + 1 === Object.keys(destinationValuesByResourceId).length) {
                            setDestinationConnectionValues(valueCleanMapping)
                            setAllDestinationConnectionValues(valueCleanMappingAll)
                            if (accordionIdentifier) {
                                setShowAccordionResource(prev => ({ ...prev, [accordionIdentifier]: true }));
                            }
                            return resolve(true)
                        }
                    })
                } else {
                    return resolve(false)
                }
            })
        })
        return result
    }

    const fetchResourceMappings = async (type = null) => {
        const result = new Promise((resolve) => {
            ApiRequest('GET', '/dmt/jobs/{jobId}/mappings', { pathParams: { jobId: jobData.id } }).then(function (results) {
                if (!results.data) return resolve([])

                var resourceMappings = results.data ? results.data : []
                jobResourceMappings.current = resourceMappings

                let tempList = [];
                if (type === 'key') {
                    tempList = resourceMappings.filter(mapping => mapping.is_setup === 0);
                } else if (type === 'value') {
                    tempList = resourceMappings.filter(mapping => mapping.is_setup === 1);
                } else {
                    tempList = resourceMappings;
                }

                const tempAccordionState = tempList.reduce((accordion, item) => {
                    accordion[`${item.source_resource_id}-${item.destination_resource_id}`] = false;
                    return accordion;
                }, {});

                setShowAccordionResource(prev => ({ ...prev, ...tempAccordionState }))

                if (resourceMappings?.length > 0) {
                    setMappedResources({
                        [jobData.source_connection.id]: createValuesArray(resourceMappings, 'source_resource_id'),
                        [jobData.destination_connection.id]: createValuesArray(resourceMappings, 'destination_resource_id'),
                    })
                    return resolve(resourceMappings)
                } else {
                    setMappedResources({
                        [jobData.source_connection.id]: [],
                        [jobData.destination_connection.id]: [],
                    })
                    return resolve([])
                }
            })
        })
        return result
    }

    // ─── Composed Pipelines ───

    const getResourceKeyMappings = asyncPipeline(fetchDestinationResourceKeys, fetchKeyMappings)
    const getResourceValueMappings = asyncPipeline(fetchDestinationResourceValues, fetchValueMappings)

    const getJobMappings = async () => {
        const mappingResults = await fetchResourceMappings()
        const [resourceKeysResult, resourceValuesResult] = await Promise.all([
            fetchKeyMappingSummary(),
            fetchValueMappingSummary(),
        ])
        return resourceKeysResult || resourceValuesResult
    }

    const fetchAvailableResources = () => fetchResources(true)

    const getAllResourcesAndMappings = asyncPipeline(
        () => fetchResources(false),
        setShowResources,
        fetchSupplementaryData,
        getJobMappings,
        setRenderPage
    )

    const getEverything = async () => {
        getMappingCodes()
        getDefaultResourceMappings()
        getDMTMappingJobs()
        fetchJobReport()
        fetchKeyResourceLookup()
        await getAllResourcesAndMappings()
    }

    const getDMTMappingJobs = () => {
        ApiRequest('GET', '/dmt/jobs', { queryParams: { jobId, jobEventSource, jobEventDestination } }).then(function (results) {
            if (results.data?.length > 0) setMappingJobs(results.data)
        })
    }

    const fetchJobReport = () => {
        if (!jobData.is_archived) {
            ApiRequest('GET', '/dmt/jobs/{jobId}/report', { pathParams: { jobId } }).then(function (results) {
                setJobReport(results.data ? results.data : [])
            })
        }
    }

    return {
        // Derived
        jobEventSource,
        jobEventDestination,

        // State
        mappingJobs,
        jobReport,
        allJobs,
        jobResources,
        mappedResources,
        jobResourceMappings,
        jobKeyMappings,
        jobKeyMappingsLength,
        showJobKeyMappings, setShowJobKeyMappings,
        showDeletedJobKeyMappings, setShowDeletedJobKeyMappings,
        keyMappingSummary,

        jobValueMappings,
        jobValueMappingsLength,
        showJobValueMappings, setShowJobValueMappings,
        valueMappingSummary,
        jobValueMappingDestinationUsage,
        supplementaryData, setSupplementaryData,
        supplementaryDataLength,
        showSupplementaryData, setShowSupplementaryData,
        compositeChains,
        availableResources,
        showResources, setShowResources,
        showResourceMappings, setShowResourceMappings,
        destinationConnectionKeys,
        allDestinationConnectionKeys,
        destinationConnectionValues,
        allDestinationConnectionValues,
        destinationResourceParentScoped,
        mappingCodes,
        mappingCodeOptions,
        dmtStates,
        dmtCompositeStates,
        isArchived, setIsArchived,
        defaultResourceMappings,
        renderPage, setRenderPage,
        showAccordionResource, setShowAccordionResource,
        dataPreview, setDataPreview,
        keyResourceLookup,
        isOpen, isOpen2, isOpen3,
        toggle, toggle2, toggle3,

        // Fetch functions
        getMappingCodes,
        getDefaultResourceMappings,
        fetchDMTJobs,
        fetchKeyResourceLookup,
        fetchResources,
        fetchKeyMappings,
        fetchKeyMappingSummary,
        fetchDestinationResourceKeys,
        fetchSupplementaryData,
        fetchValueMappings,
        fetchValueMappingSummary,
        fetchDestinationResourceValues,
        fetchResourceMappings,
        fetchJobReport,

        // Pipelines
        getResourceKeyMappings,
        getResourceValueMappings,
        getJobMappings,
        getAllResourcesAndMappings,
        getEverything,
        getDMTMappingJobs,
        fetchAvailableResources,
    }
}

export default useDMTData;
